import { GoogleGenerativeAI } from '@google/generative-ai';
import { toast } from 'react-toastify';

class GeminiDirectService {
  constructor() {
    this.genAI = null;
    this.initializeAPI();
  }

  initializeAPI() {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey) {
      console.warn('REACT_APP_GEMINI_API_KEY not found. Gemini direct services will not be available.');
      return;
    }
    
    try {
      this.genAI = new GoogleGenerativeAI(apiKey);
    } catch (error) {
      console.error('Failed to initialize Gemini API:', error);
    }
  }

  isAvailable() {
    return this.genAI !== null;
  }

  // Image Generation using SVG fallback (since Imagen 4.0 not available in current SDK)
  async generateImage(prompt, options = {}) {
    if (!this.isAvailable()) {
      throw new Error('Gemini API not available. Please set REACT_APP_GEMINI_API_KEY environment variable.');
    }

    try {
      toast.info('🎨 Generating educational SVG illustration...', { autoClose: 5000 });
      
      // Generate SVG using Gemini's text generation capabilities
      const svgPrompt = `Create a detailed SVG code for an educational illustration about: "${prompt}".
        
        Requirements:
        - Style: ${options.style || 'clean educational illustration'}
        - Use bright, educational colors (blues, greens, oranges)
        - Include clear labels and text
        - Make it suitable for grade school students
        - Size: 400x300 viewBox
        - Include educational icons and symbols
        - Use proper SVG syntax with <svg> tags
        
        Return ONLY the complete SVG code, nothing else:`;

      const model = this.genAI.getGenerativeModel({ model: "gemini-1.5-flash" });
      const result = await model.generateContent(svgPrompt);
      const response = await result.response;
      const svgContent = response.text();
      
      // Clean up the SVG content
      let cleanSvg = svgContent.trim();
      if (cleanSvg.includes('```svg')) {
        cleanSvg = cleanSvg.replace(/```svg\n?/g, '').replace(/```/g, '');
      }
      if (cleanSvg.includes('```')) {
        cleanSvg = cleanSvg.replace(/```/g, '');
      }
      
      // Ensure proper SVG structure
      if (!cleanSvg.startsWith('<svg')) {
        throw new Error('Generated content is not valid SVG');
      }

      // Convert SVG to base64 for consistent handling
      const svgBase64 = btoa(unescape(encodeURIComponent(cleanSvg)));
      
      return {
        success: true,
        data: {
          imageBase64: svgBase64,
          svgContent: cleanSvg,
          description: prompt,
          format: 'svg',
          mimeType: 'image/svg+xml',
          filename: `educational_visual_${Date.now()}.svg`,
          source: 'Gemini SVG Generator'
        }
      };
    } catch (error) {
      console.error('Gemini SVG generation failed:', error);
      
      // Fallback to SVG if Imagen fails
      toast.warn('Imagen API failed, using SVG fallback...', { autoClose: 3000 });
      return this.generateSvgFallback(prompt, options);
    }
  }

  // SVG Fallback for image generation
  async generateSvgFallback(prompt, options = {}) {
    try {
      const model = this.genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });
      
      const response = await model.generateContent(`Create a detailed educational description for: ${prompt}. 
                 Make it suitable for classroom use, visually clear, and educational.
                 Style: ${options.style || 'educational diagram'}`);

      const description = response.response.text();
      const placeholderSvg = this.createEducationalSvg(prompt, description);
      
      return {
        success: true,
        data: {
          imageBase64: btoa(placeholderSvg),
          svgContent: placeholderSvg,
          description: description,
          format: 'svg',
          mimeType: 'image/svg+xml',
          filename: `${prompt.replace(/[^a-z0-9]/gi, '_')}_fallback.svg`,
          source: 'Gemini SVG Fallback'
        }
      };
    } catch (error) {
      throw new Error(`Both Imagen and SVG fallback failed: ${error.message}`);
    }
  }

  // Text-to-Speech using Gemini 2.5 Pro TTS
  async generateAudio(text, language = 'English', options = {}) {
    if (!this.isAvailable()) {
      throw new Error('Gemini API not available. Please set REACT_APP_GEMINI_API_KEY environment variable.');
    }

    try {
      toast.info('🎵 Generating audio with Gemini 2.5 Pro TTS...', { autoClose: 5000 });
      
      // First enhance the script
      const model = this.genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });
      
      const scriptPrompt = `Convert this text to a natural, educational audio script in ${language}:
        "${text}"
        
        Make it:
        - Clear and engaging for students
        - Properly paced for learning
        - Include natural pauses
        - Maintain educational tone
        - Add appropriate emphasis
        
        Return only the enhanced script text.`;

      const scriptResult = await model.generateContent(scriptPrompt);
      const enhancedScript = scriptResult.response.text();
      
      // Configure TTS based on language
      const voiceConfig = this.getVoiceConfig(language);
      
      try {
        // Use Gemini 2.5 Pro TTS
        const ttsConfig = {
          temperature: 1,
          responseModalities: ['audio'],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: {
                voiceName: voiceConfig.voiceName
              }
            }
          }
        };

        // Note: TTS API is not available in this SDK version
        // Use text fallback for now
        throw new Error('Gemini TTS not available - using text fallback');

        let audioChunks = [];
        let fileIndex = 0;
        
        for await (const chunk of ttsResponse) {
          if (chunk.candidates?.[0]?.content?.parts?.[0]?.inlineData) {
            const inlineData = chunk.candidates[0].content.parts[0].inlineData;
            if (inlineData.mimeType?.startsWith('audio/')) {
              audioChunks.push(inlineData.data);
            }
          }
        }

        if (audioChunks.length === 0) {
          throw new Error('No audio data generated');
        }

        // Combine audio chunks
        const combinedAudio = audioChunks.join('');
        const filename = `audio_${language}_gemini_tts_${Date.now()}.wav`;
        
        return {
          success: true,
          data: {
            originalTopic: text,
            generatedScript: enhancedScript,
            audioBase64: combinedAudio,
            downloadUrl: `data:audio/wav;base64,${combinedAudio}`,
            filename: filename,
            mimeType: 'audio/wav',
            fileSize: Math.ceil(combinedAudio.length * 0.75), // Rough estimate
            duration: Math.ceil(enhancedScript.length / 15), // Rough estimate
            language: language,
            voice: voiceConfig.voiceName,
            source: 'Gemini 2.5 Pro TTS'
          }
        };
      } catch (ttsError) {
        console.warn('Gemini TTS failed, using text fallback:', ttsError);
        // Fallback to text-only response
        return this.generateAudioFallback(text, language, enhancedScript);
      }
    } catch (error) {
      console.error('Gemini audio generation failed:', error);
      throw new Error(`Audio generation failed: ${error.message}`);
    }
  }

  // Voice configuration based on language
  getVoiceConfig(language) {
    const configs = {
      'English': { voiceName: 'Puck', tone: 'warm, educational' },
      'Hindi': { voiceName: 'Zephyr', tone: 'clear, friendly' },
      'Marathi': { voiceName: 'Puck', tone: 'warm, clear' },
      'Gujarati': { voiceName: 'Zephyr', tone: 'gentle, educational' }
    };
    
    return configs[language] || configs['English'];
  }

  // Audio fallback when TTS fails
  async generateAudioFallback(originalText, language, enhancedScript) {
    toast.warn('Using text-based audio fallback...', { autoClose: 3000 });
    
    const audioPlaceholder = this.createAudioPlaceholder(enhancedScript, language);
    
    return {
      success: true,
      data: {
        originalTopic: originalText,
        generatedScript: enhancedScript,
        audioBase64: audioPlaceholder.base64,
        downloadUrl: audioPlaceholder.dataUrl,
        filename: `audio_${language}_fallback_${Date.now()}.txt`,
        mimeType: 'text/plain',
        fileSize: audioPlaceholder.size,
        duration: Math.ceil(enhancedScript.length / 10),
        language: language,
        voice: 'Text Fallback',
        source: 'Gemini Text Fallback'
      }
    };
  }

  // Text Generation using Gemini
  async generateContent(concept, gradeLevel, language = 'English') {
    if (!this.isAvailable()) {
      throw new Error('Gemini API not available. Please set REACT_APP_GEMINI_API_KEY environment variable.');
    }

    try {
      toast.info('📝 Generating content with Gemini AI...', { autoClose: 3000 });
      
      const model = this.genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });
      
      const prompt = `Create educational content about "${concept}" for Grade ${gradeLevel} students in ${language}.

Requirements:
- Age-appropriate for Grade ${gradeLevel}
- Clear and engaging explanation
- Include key concepts and examples
- Educational and informative
- Length: 200-400 words
- Use simple language suitable for the grade level

Format as a comprehensive story or explanation that helps students understand the concept clearly.`;

      const result = await model.generateContent(prompt);
      const content = result.response.text();
      
      // Clean up the content
      const cleanContent = content.trim();
      
      return {
        success: true,
        data: {
          story: cleanContent,
          title: `Learning About ${concept}`,
          mainContent: cleanContent,
          model: 'gemini-1.5-flash-direct',
          generationTime: '2-3 seconds',
          cached: false,
          language: language,
          gradeLevel: gradeLevel,
          concept: concept,
          source: 'Gemini Direct (Frontend)'
        }
      };
    } catch (error) {
      console.error('Gemini content generation failed:', error);
      throw new Error(`Content generation failed: ${error.message}`);
    }
  }

  // Worksheet Generation using Gemini
  async generateWorksheet(concept, grades, language = 'English') {
    if (!this.isAvailable()) {
      throw new Error('Gemini API not available. Please set REACT_APP_GEMINI_API_KEY environment variable.');
    }

    try {
      toast.info('📋 Generating worksheets with Gemini AI...', { autoClose: 3000 });
      
      const model = this.genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });
      
      const worksheets = [];
      
      for (const grade of grades) {
        const prompt = `Create a simple worksheet about "${concept}" for Grade ${grade} students in ${language}.

Include:
- Clear title: "${concept} - Grade ${grade} Worksheet"
- Simple instructions for students
- 5-6 questions appropriate for Grade ${grade}
- Mix of multiple choice, fill-in-blanks, and short answers
- Answer key at the end
- Make it educational and age-appropriate

Format as plain text that is easy to read and understand.`;

        const result = await model.generateContent(prompt);
        const content = result.response.text().trim();
        
        // Create a clean filename
        const cleanConcept = concept.replace(/[^a-zA-Z0-9]/g, '_');
        const filename = `${cleanConcept}_Grade_${grade}_${language}_${Date.now()}.txt`;
        
        worksheets.push({
          grade: grade,
          content: content,
          filename: filename,
          downloadUrl: `data:text/plain;charset=utf-8,${encodeURIComponent(content)}`,
          format: 'text',
          mimeType: 'text/plain',
          fileSize: content.length,
          source: 'Gemini Direct',
          title: `${concept} - Grade ${grade} Worksheet`,
          language: language
        });
      }
      
      return {
        success: true,
        data: {
          topic: concept,
          worksheets: worksheets,
          totalGenerated: worksheets.length,
          language: language,
          grades: grades,
          source: 'Gemini Direct (Frontend)',
          method: 'Simple Gemini Generation'
        }
      };
    } catch (error) {
      console.error('Gemini worksheet generation failed:', error);
      throw new Error(`Worksheet generation failed: ${error.message}`);
    }
  }

  // Helper method to create educational SVG
  createEducationalSvg(prompt, description) {
    const cleanPrompt = prompt.replace(/[<>&'"]/g, '');
    const cleanDescription = description.substring(0, 200).replace(/[<>&'"]/g, '');
    
    return `<svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#e3f2fd;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#bbdefb;stop-opacity:1" />
        </linearGradient>
      </defs>
      
      <rect width="400" height="300" fill="url(#bgGrad)" stroke="#1976d2" stroke-width="2" rx="10"/>
      
      <text x="200" y="40" text-anchor="middle" font-family="Arial" font-size="18" font-weight="bold" fill="#1565c0">
        ${cleanPrompt}
      </text>
      
      <rect x="50" y="60" width="300" height="180" fill="white" stroke="#42a5f5" stroke-width="1" rx="5"/>
      
      <foreignObject x="60" y="70" width="280" height="160">
        <div xmlns="http://www.w3.org/1999/xhtml" style="font-family: Arial; font-size: 12px; color: #333; padding: 10px; line-height: 1.4;">
          ${cleanDescription}
        </div>
      </foreignObject>
      
      <text x="200" y="270" text-anchor="middle" font-family="Arial" font-size="10" fill="#666">
        Generated by Gemini AI - Educational Visual
      </text>
    </svg>`;
  }

  // Helper method to create audio placeholder
  createAudioPlaceholder(text, language) {
    // Create a simple data URL for audio placeholder
    const placeholder = `Audio content for: ${text.substring(0, 50)}... (Language: ${language})`;
    const base64 = btoa(placeholder);
    
    return {
      base64: base64,
      dataUrl: `data:text/plain;base64,${base64}`,
      size: placeholder.length
    };
  }

  // Video Generation using Veo 2.0
  async generateVideo(prompt, options = {}) {
    if (!this.isAvailable()) {
      throw new Error('Gemini API not available. Please set REACT_APP_GEMINI_API_KEY environment variable.');
    }

    try {
      toast.info('🎬 Generating video with Veo 2.0... This may take several minutes.', { autoClose: 10000 });
      
      // Enhanced prompt for educational video
      const educationalPrompt = `Educational video: ${prompt}. 
        Create a clear, engaging, and informative video suitable for students.
        Style: Educational, professional, with clear visuals that aid learning.
        Focus on key concepts and visual demonstrations.`;
      
      // Note: Video generation API is not available in this SDK version
      throw new Error('Video generation not available in current SDK');

      // Poll for completion
      toast.info('🔄 Video generation in progress... Please wait.', { autoClose: 5000 });
      
      let attempts = 0;
      const maxAttempts = 60; // 10 minutes max
      
      while (!operation.done && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 10000)); // Wait 10 seconds
        
        operation = await this.genAI.operations.getVideosOperation({
          operation: operation,
        });
        
        attempts++;
        if (attempts % 3 === 0) { // Update every 30 seconds
          toast.info(`🎬 Still generating video... (${attempts * 10}s elapsed)`, { autoClose: 3000 });
        }
      }

      if (!operation.done) {
        throw new Error('Video generation timed out after 10 minutes');
      }

      if (!operation.response?.generatedVideos?.length) {
        throw new Error('No videos were generated');
      }

      const generatedVideo = operation.response.generatedVideos[0];
      if (!generatedVideo?.video?.uri) {
        throw new Error('Video URI not available');
      }

      // Download the video
      toast.info('📥 Downloading generated video...', { autoClose: 3000 });
      
      const videoUrl = `${generatedVideo.video.uri}&key=${process.env.REACT_APP_GEMINI_API_KEY}`;
      const response = await fetch(videoUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to download video: ${response.statusText}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const videoBase64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
      const filename = `video_${prompt.replace(/[^a-z0-9]/gi, '_')}_veo2.mp4`;
      
      return {
        success: true,
        data: {
          videoBase64: videoBase64,
          videoUrl: generatedVideo.video.uri,
          downloadUrl: `data:video/mp4;base64,${videoBase64}`,
          filename: filename,
          mimeType: 'video/mp4',
          fileSize: arrayBuffer.byteLength,
          duration: options.duration || 8,
          description: prompt,
          source: 'Veo 2.0 (Gemini AI Studio Pro)'
        }
      };
      
    } catch (error) {
      console.error('Gemini video generation failed:', error);
      throw new Error(`Video generation failed: ${error.message}`);
    }
  }
}

export default GeminiDirectService;